import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  Activity,
  Clock,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  HardDrive,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import api from '@/lib/api';
import { AnalyticsPageSkeleton } from '@/components/ui/page-skeletons';
import { StorageCard } from '@/components/analytics/StorageCard';
import type { StorageStats } from '@/types/storage-stats';

const COLORS = ['#10b981', '#ef4444', '#3b82f6', '#f59e0b', '#8b5cf6'];

const STATUS_COLORS: Record<string, string> = {
  completed: '#10b981',
  failed: '#ef4444',
  running: '#3b82f6',
  waiting: '#f59e0b',
  cancelled: '#6b7280',
};

interface AnalyticsSummary {
  totalExecutions: number;
  successCount: number;
  failedCount: number;
  runningCount: number;
  waitingCount: number;
  cancelledCount: number;
  avgDuration: number;
  activeWorkflows: number;
}

interface ExecutionTrend {
  date: string;
  total: number;
  success: number;
  failed: number;
}

interface TopWorkflow {
  workflowId: string;
  workflowName: string;
  executionCount: number;
  successCount: number;
  avgDuration: number;
}

interface TriggerType {
  type: string;
  count: number;
}

interface NodeUsage {
  nodeType: string;
  executionCount: number;
  successCount: number;
  avgDuration: number;
}

interface RecentExecution {
  id: string;
  workflowId: string;
  workflowName: string;
  status: string;
  triggerType: string;
  startedAt: number;
  completedAt: number;
  createdAt: number;
  error: string | null;
}

export default function Analytics() {
  const backendUrl = useGetBackendUrl();
  const [activeTab, setActiveTab] = useState<'activity' | 'storage'>('activity');
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [loading, setLoading] = useState(true);
  const [storageLoading, setStorageLoading] = useState(false);

  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [trends, setTrends] = useState<ExecutionTrend[]>([]);
  const [topWorkflows, setTopWorkflows] = useState<TopWorkflow[]>([]);
  const [triggerTypes, setTriggerTypes] = useState<TriggerType[]>([]);
  const [nodeUsage, setNodeUsage] = useState<NodeUsage[]>([]);
  const [recentExecutions, setRecentExecutions] = useState<RecentExecution[]>([]);
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);

  // Convert timeRange to days for API calls
  const getDaysFromTimeRange = (range: '24h' | '7d' | '30d' | '90d'): number => {
    switch (range) {
      case '24h': return 1;
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 7;
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const days = getDaysFromTimeRange(timeRange);
      
      const [
        summaryRes,
        trendsRes,
        workflowsRes,
        triggersRes,
        nodesRes,
        recentRes,
      ] = await Promise.all([
        api.get('/api/analytics/summary'),
        api.get('/api/analytics/trends', { params: { days } }),
        api.get('/api/analytics/top-workflows', { params: { days, limit: 10 } }),
        api.get('/api/analytics/trigger-types', { params: { days } }),
        api.get('/api/analytics/node-usage', { params: { limit: 10 } }),
        api.get('/api/analytics/recent-executions', { params: { limit: 10 } }),
      ]);

      setSummary(summaryRes.data);
      setTrends(trendsRes.data);
      setTopWorkflows(workflowsRes.data);
      setTriggerTypes(triggersRes.data);
      setNodeUsage(nodesRes.data);
      setRecentExecutions(recentRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageStats = async () => {
    setStorageLoading(true);
    try {
      const response = await api.get('/api/analytics/storage');
      setStorageStats(response.data);
    } catch (error) {
      console.error('Failed to load storage stats:', error);
    } finally {
      setStorageLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  // Fetch storage stats when switching to storage tab (or on mount if storage tab is active)
  useEffect(() => {
    if (activeTab === 'storage' && !storageStats) {
      fetchStorageStats();
    }
  }, [activeTab]);

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'N/A';
    // Handle both seconds and milliseconds timestamps
    const date = timestamp < 10000000000 ? new Date(timestamp * 1000) : new Date(timestamp);
    return date.toLocaleString();
  };

  // Format node/trigger types to pretty names (e.g., "schedule-trigger" → "Schedule Trigger")
  const formatPrettyName = (name: string) => {
    if (!name) return name;
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Transform data for charts with pretty names
  const formattedNodeUsage = nodeUsage.map(n => ({
    ...n,
    prettyName: formatPrettyName(n.nodeType)
  }));

  const formattedTriggerTypes = triggerTypes.map(t => ({
    ...t,
    prettyName: formatPrettyName(t.type)
  }));

  const successRate = summary
    ? summary.totalExecutions > 0
      ? ((summary.successCount / summary.totalExecutions) * 100).toFixed(1)
      : '0'
    : '0';

  // Prepare status distribution data for pie chart
  const statusDistribution = summary
    ? [
        { name: 'Success', value: summary.successCount, color: STATUS_COLORS.completed },
        { name: 'Failed', value: summary.failedCount, color: STATUS_COLORS.failed },
        { name: 'Running', value: summary.runningCount, color: STATUS_COLORS.running },
        { name: 'Waiting', value: summary.waitingCount, color: STATUS_COLORS.waiting },
        { name: 'Cancelled', value: summary.cancelledCount, color: STATUS_COLORS.cancelled },
      ].filter(item => item.value > 0)
    : [];

  if (loading) {
    return <AnalyticsPageSkeleton />;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <BarChart3 className="w-8 h-8" />
              Analytics
            </h1>
            <p className="text-muted-foreground mt-1">
              Workflow execution insights and performance metrics
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'activity' && (
              <>
                <div className="flex gap-1 bg-muted p-1 rounded-lg">
                  {(['24h', '7d', '30d', '90d'] as const).map((range) => (
                    <Button
                      key={range}
                      variant={timeRange === range ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setTimeRange(range)}
                    >
                      {range}
                    </Button>
                  ))}
                </div>
                <Button variant="outline" size="icon" onClick={fetchAnalytics}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </>
            )}
            {activeTab === 'storage' && (
              <Button variant="outline" size="icon" onClick={fetchStorageStats}>
                <RefreshCw className={`w-4 h-4 ${storageLoading ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'activity' | 'storage')}>
          <TabsList>
            <TabsTrigger value="activity">
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="storage">
              <HardDrive className="w-4 h-4 mr-2" />
              Storage
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="space-y-6 mt-6">
            {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Executions</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.totalExecutions || 0}</div>
                <p className="text-xs text-muted-foreground">
                  In the last {timeRange}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{successRate}%</div>
                <p className="text-xs text-muted-foreground">
                  {summary?.successCount || 0} successful executions
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatDuration(summary?.avgDuration || 0)}</div>
                <p className="text-xs text-muted-foreground">
                  Average execution time
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Workflows</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.activeWorkflows || 0}</div>
                <p className="text-xs text-muted-foreground">
                  Workflows with executions
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Execution Trends */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card>
              <CardHeader>
                <CardTitle>Execution Trends</CardTitle>
                <CardDescription>Daily execution counts over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="success" stackId="1" stroke="#10b981" fill="#10b981" name="Success" />
                    <Area type="monotone" dataKey="failed" stackId="1" stroke="#ef4444" fill="#ef4444" name="Failed" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* Status Distribution */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <Card>
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
                <CardDescription>Breakdown of execution statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent as number) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Charts Row 2 - Most Used Nodes and Trigger Types */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Most Used Nodes */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Most Used Nodes</CardTitle>
                <CardDescription>Top 10 nodes by execution count</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={formattedNodeUsage} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="prettyName"
                      type="category"
                      width={130}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value: number) => [value.toLocaleString(), 'Executions']}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="executionCount" fill="#8b5cf6" name="Executions" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* Trigger Types */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Trigger Types</CardTitle>
                <CardDescription>How workflows are initiated</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={formattedTriggerTypes} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="prettyName"
                      type="category"
                      width={100}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value: number) => [value.toLocaleString(), 'Count']}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="count" fill="#3b82f6" name="Count" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tables Row 3 - Top Workflows and Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Workflows */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Top Workflows</CardTitle>
                <CardDescription>Most executed workflows</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 h-[280px] overflow-y-auto pr-2">
                  {topWorkflows.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No workflow data available</p>
                  ) : (
                    topWorkflows.map((workflow, index) => (
                      <div key={workflow.workflowId} className="flex items-center justify-between pb-3 border-b last:border-0">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{index + 1}.</span>
                            <span className="text-sm font-medium truncate">{workflow.workflowName}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">
                              {workflow.executionCount} executions
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-green-600">
                              {((workflow.successCount / workflow.executionCount) * 100).toFixed(0)}% success
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDuration(workflow.avgDuration)} avg
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Activity */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest workflow executions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 h-[280px] overflow-y-auto pr-2">
                  {recentExecutions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No recent executions</p>
                  ) : (
                    recentExecutions.map((execution) => (
                      <div key={execution.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
                        <div className="mt-1">
                          {execution.status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                          {execution.status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                          {execution.status === 'running' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
                          {execution.status === 'waiting' && <Clock className="w-4 h-4 text-yellow-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{execution.workflowName}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {formatPrettyName(execution.triggerType)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(execution.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
          </TabsContent>

          <TabsContent value="storage" className="mt-6">
            {storageLoading && !storageStats ? (
              <Card className="p-8">
                <div className="flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              </Card>
            ) : storageStats ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <StorageCard data={storageStats} />
              </motion.div>
            ) : (
              <Card className="p-8">
                <p className="text-center text-muted-foreground">
                  Failed to load storage statistics
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
